import React from 'react';
import styled from 'styled-components';

const LeadsInfo = styled.div`
    margin-bottom: 2rem;
    padding: 1.6rem;
    background: ${props => props.theme.background || 'transparent'};
    border: 0.1rem solid ${props => props.theme.mid || 'rgba(0, 0, 0, 0.12)'};
    border-radius: 1rem;
`;

const LeadsInfoGrid = styled.div`
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 1.6rem 2.4rem;

    [data-field="username"] {
        grid-column: span 2;
    }

    @media (max-width: 75rem) {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    @media (max-width: 50rem) {
        grid-template-columns: minmax(0, 1fr);

        [data-field="username"] {
            grid-column: span 1;
        }
    }
`;

const LeadsInfoField = styled.div`
    display: grid;
    gap: 0.4rem;
    min-width: 0;

    p {
        line-height: 1.4;
        word-break: break-word;
        white-space: pre-wrap;
    }
`;

const LeadsInfoLabel = styled.div`
    color: ${props => props.theme.color};
    font-family: "fontBold";
    font-size: 1.4rem;
    line-height: 1.3;
`;

/**
 * Returns the input text as one trimmed display value.
 */
const read_text_value = (value) => {
    return `${value || ''}`.trim();
};

/**
 * Returns the first non-empty value from modern and legacy Lead fields.
 */
const read_first_value = (...values) => {
    return values.map((value) => read_text_value(value)).filter(Boolean)[0] || '';
};

/**
 * Returns a readable date label without showing invalid date text.
 */
const read_date_value = (value) => {
    const date = new Date(value);
    const timestamp = date.getTime();
    const readers = {
        true: () => '',
        false: () => date.toLocaleDateString('el-GR')
    };

    return readers[Number.isNaN(timestamp)]();
};

/**
 * Reads a source label as the username before the email domain.
 */
const read_source_username = (source) => {
    const sourceValue = read_text_value(source);

    if (!sourceValue.includes('@')) {
        return sourceValue;
    }

    return sourceValue.split('@')[0].trim();
};

/**
 * Resolves first-name source labels to the matching Growth username.
 */
const read_source_label = (source, members) => {
    const sourceValue = read_source_username(source);
    const matchedMember = (members || []).find((member) => {
        const firstName = read_text_value(member?.firstname || member?.firstName);
        const fullName = [member?.firstname || member?.firstName, member?.lastname || member?.lastName].filter(Boolean).join(' ');

        return sourceValue === firstName || sourceValue === fullName;
    });

    if (!matchedMember) {
        return sourceValue;
    }

    return read_source_username(matchedMember?.email || matchedMember?.username) || sourceValue;
};

/**
 * Returns the display value or the translated empty-state fallback.
 */
const read_field_value = (value, fallback) => {
    return read_text_value(value) || fallback;
};

/**
 * Keeps tests and defensive usage readable when no translator is passed.
 */
const read_translation = (value) => {
    return value;
};

/**
 * Builds Lead metadata in a stable identity-first layout.
 */
const read_lead_info_fields = (lead, t, members) => {
    return [
        { key: 'firstName', label: t('First name'), value: read_first_value(lead?.firstName, lead?.firstname) },
        { key: 'lastName', label: t('Last name'), value: read_first_value(lead?.lastName, lead?.lastname) },
        { key: 'username', label: t('Username'), value: read_first_value(lead?.username, lead?.email) },
        { key: 'registrationStatus', label: t('Registration'), value: read_field_value(read_first_value(lead?.registrationStatus), t('unregistered')) },
        { key: 'source', label: t('Source'), value: read_field_value(read_source_label(lead?.source, members), t('No source')) },
        { key: 'createdAt', label: t('Created'), value: read_field_value(read_date_value(lead?.legacyCreatedAt || lead?.createdAt), t('No created date')) },
        { key: 'updatedAt', label: t('Updated'), value: read_field_value(read_date_value(lead?.updatedAt), t('No updated date')) }
    ];
};

/**
 * Renders one read-only Lead information field.
 */
const Leads_Info_Field = (props) => {
    return (
        <LeadsInfoField data-field={props?.field?.key}>
            <LeadsInfoLabel>{props?.field?.label}</LeadsInfoLabel>
            <p>{props?.field?.value}</p>
        </LeadsInfoField>
    );
};

/**
 * Renders the Lead-level information above the nested Brand cards.
 */
export const Leads_Info = (props) => {
    const t = props?.t || read_translation;
    const fields = read_lead_info_fields(props?.selectedLead, t, props?.growthMembers);

    return (
        <LeadsInfo>
            <LeadsInfoGrid>
                {fields.map((field) => (
                    <Leads_Info_Field
                        key={field.key}
                        field={field}
                    />
                ))}
            </LeadsInfoGrid>
        </LeadsInfo>
    );
};
